import { useState } from 'react';
import { User } from 'firebase/auth';
import { updateProjectDomains } from '../services/projects';
import { SectionCard } from './ui/SectionCard';
import { trackEvent } from '../firebase';

interface Props {
  user: User;
  projectId: string;
  initialDomains: string[];
}

const VALID_DOMAIN_REGEX =
  /^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?(\.[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?)+$/;

function isValidDomain(domain: string): boolean {
  return domain.length <= 253 && VALID_DOMAIN_REGEX.test(domain);
}

export function DomainManager({ user, projectId, initialDomains }: Props) {
  const [domains, setDomains] = useState<string[]>(initialDomains);
  const [input, setInput] = useState('');
  const [inputError, setInputError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [expanded, setExpanded] = useState(false);

  function addDomain() {
    const raw = input.trim().toLowerCase();
    if (!raw) return;
    if (!isValidDomain(raw)) {
      setInputError('Enter a valid domain (e.g. company.com).');
      return;
    }
    if (domains.includes(raw)) {
      setInputError('Domain already added.');
      return;
    }
    setDomains((prev) => [...prev, raw]);
    setInput('');
    setInputError(null);
  }

  function removeDomain(domain: string) {
    setDomains((prev) => prev.filter((d) => d !== domain));
  }

  async function save() {
    setSaving(true);
    setSaveError(null);
    setSaveSuccess(false);
    try {
      const saved = await updateProjectDomains(user, projectId, domains);
      setDomains(saved);
      setSaveSuccess(true);
      trackEvent('update_allowed_domains', { project_id: projectId });
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Failed to save.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <SectionCard
      title="Domain protection"
      actions={
        <button
          onClick={() => setExpanded((v) => !v)}
          className="text-sm text-brand-500 font-medium hover:underline"
          aria-expanded={expanded}
        >
          {expanded ? 'Hide' : 'Configure'}
        </button>
      }
    >
      {!expanded ? (
        <p className="text-sm text-gray-500">
          {domains.length === 0
            ? 'All domains are allowed. Configure to restrict which sites can embed your waitlist.'
            : `${domains.length} domain${domains.length === 1 ? '' : 's'} allowlisted.`}
        </p>
      ) : (
        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            When domains are added, only requests from those sites (matched by hostname) can submit
            to your waitlist. Subdomains are automatically permitted. Leave empty to allow everyone.
          </p>

          {/* Add domain input */}
          <div className="flex gap-2">
            <input
              type="text"
              value={input}
              onChange={(e) => {
                setInput(e.target.value);
                setInputError(null);
              }}
              onKeyDown={(e) => e.key === 'Enter' && addDomain()}
              placeholder="company.com"
              className="flex-1 border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
              aria-label="Domain to add"
            />
            <button
              onClick={addDomain}
              className="px-4 py-2 bg-brand-500 hover:bg-brand-600 text-white text-sm font-medium rounded-md transition-colors"
            >
              Add
            </button>
          </div>
          {inputError && <p className="text-xs text-red-500">{inputError}</p>}

          {/* Domain list */}
          {domains.length > 0 && (
            <ul className="space-y-2">
              {domains.map((domain) => (
                <li
                  key={domain}
                  className="flex items-center justify-between bg-gray-50 border border-gray-200 rounded-md px-3 py-2"
                >
                  <span className="text-sm font-mono text-gray-800">{domain}</span>
                  <button
                    onClick={() => removeDomain(domain)}
                    className="text-gray-400 hover:text-red-500 transition-colors"
                    aria-label={`Remove ${domain}`}
                  >
                    ✕
                  </button>
                </li>
              ))}
            </ul>
          )}

          {/* Save */}
          <div className="flex items-center gap-3">
            <button
              onClick={save}
              disabled={saving}
              className="px-4 py-2 bg-brand-500 hover:bg-brand-600 disabled:opacity-50 text-white text-sm font-medium rounded-md transition-colors"
            >
              {saving ? 'Saving…' : 'Save'}
            </button>
            {saveSuccess && <span className="text-sm text-green-600 font-medium">Saved ✓</span>}
            {saveError && <span className="text-sm text-red-500">{saveError}</span>}
          </div>
        </div>
      )}
    </SectionCard>
  );
}
